"use client";

import { c as _c } from "react/compiler-runtime";
import { getFileInfoFromUrl } from "@/helpers/file.helpers";
import { shareFetchedBlobInNativeApp } from "@/helpers/capacitorBlobDownload.helpers";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import CommonDropdownItemsDefaultWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsDefaultWrapper";
import JsonPreview from "@/components/drops/view/item/content/attachments/JsonPreview";
import { faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ArrowDownTrayIcon, CodeBracketSquareIcon, DocumentIcon, EllipsisHorizontalIcon, EyeIcon, EyeSlashIcon, LinkIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import useCapacitor from "@/hooks/useCapacitor";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import clsx from "clsx";
import { Tooltip } from "react-tooltip";
import type { ReactNode, RefObject } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
const SAFE_URL_PROTOCOLS = new Set(["https:", "http:", "blob:"]);
function getSafeAttachmentUrl(rawUrl: string): string | null {
  if (rawUrl) {
    try {
      const resolvedUrl = resolveIpfsUrlSync(rawUrl);
      const parsed = new URL(resolvedUrl, globalThis.window?.location.origin);
      if (SAFE_URL_PROTOCOLS.has(parsed.protocol)) {
        return parsed.toString();
      }
    } catch {
      return null;
    }
  }
  return null;
}
type AttachmentRenderType = "csv" | "pdf" | "unknown";
function getAttachmentMetadataUrl(rawUrl: string): string | null {
  const trimmedUrl = rawUrl.trim();
  if (!trimmedUrl) {
    return null;
  }
  if (trimmedUrl.toLowerCase().startsWith("ipfs://")) {
    const withoutProtocol = trimmedUrl.slice("ipfs://".length);
    const rootCid = withoutProtocol.split(/[/?#]/)[0];
    return rootCid ? `ipfs://${rootCid}/metadata.json` : null;
  }
  try {
    const base = globalThis.window?.location?.origin || "https://6529.io";
    const parsed = new URL(trimmedUrl, base);
    const pathSegments = parsed.pathname.split("/");
    const ipfsIndex = pathSegments.indexOf("ipfs");
    if (ipfsIndex >= 0 && pathSegments[ipfsIndex + 1]) {
      parsed.pathname = [...pathSegments.slice(0, ipfsIndex + 2), "metadata.json"].join("/");
      parsed.search = "";
      parsed.hash = "";
      return parsed.toString();
    }
    return null;
  } catch {
    return null;
  }
}
const CSV_PREVIEW_MAX_ROWS = 51;
const CSV_PREVIEW_MAX_COLUMNS = 12;
const CSV_PREVIEW_MAX_CHARS = 500_000;
const CSV_PREVIEW_SIZE_EXCEEDED = "__CSV_PREVIEW_SIZE_EXCEEDED__";
const TRUSTED_BADGE_BUTTON_CLASS = "tw-relative tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-emerald-500/20 tw-bg-emerald-500/10 tw-text-emerald-400 tw-transition-colors tw-duration-200 tw-ease-out focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-emerald-500/20 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-iron-950 desktop-hover:hover:tw-border-emerald-400/30 desktop-hover:hover:tw-bg-emerald-500/15 desktop-hover:hover:tw-text-emerald-300";
const TRUSTED_BADGE_ICON_CLASS = "tw-flex-shrink-0 tw-text-current";
const TRUSTED_BADGE_BUTTON_SIZE_CLASS_BY_SIZE = {
  default: "tw-h-5 tw-w-5",
  compact: "tw-h-[18px] tw-w-[18px]"
} as const;
const TRUSTED_BADGE_ICON_SIZE_CLASS_BY_SIZE = {
  default: "tw-h-2.5 tw-w-2.5",
  compact: "tw-h-[9px] tw-w-[9px]"
} as const;
const CSV_PREVIEW_TIMEOUT_MESSAGE = "CSV preview timed out. Please download the file.";
const CSV_PREVIEW_SIZE_MESSAGE = "CSV preview exceeds the browser size limit. Please download the file.";
function TrustedAttachmentBadge(t0) {
  const $ = _c(18);
  const {
    size: t1
  } = t0;
  const size = t1 === undefined ? "default" : t1;
  const isMobile = useIsMobileDevice();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const id = useId();
  const tooltipId = `trusted-attachment-badge-${id}`;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const showTooltip = !isMobile && !hasTouchScreen;
  const dataTooltipId = showTooltip ? tooltipId : undefined;
  const describedById = showTooltip && isTooltipOpen ? tooltipId : undefined;
  const buttonSizeClassName = TRUSTED_BADGE_BUTTON_SIZE_CLASS_BY_SIZE[size];
  const iconSizeClassName = TRUSTED_BADGE_ICON_SIZE_CLASS_BY_SIZE[size];
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsTooltipOpen(true);
    t3 = () => setIsTooltipOpen(false);
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  const t4 = `${TRUSTED_BADGE_BUTTON_CLASS} ${buttonSizeClassName}`;
  let t5;
  if ($[2] !== dataTooltipId) {
    t5 = dataTooltipId && {
      "data-tooltip-id": dataTooltipId
    };
    $[2] = dataTooltipId;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = `${TRUSTED_BADGE_ICON_CLASS} ${iconSizeClassName}`;
  let t7;
  if ($[4] !== t6) {
    t7 = <FontAwesomeIcon icon={faShieldHalved} className={t6} />;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== describedById || $[7] !== t4 || $[8] !== t5 || $[9] !== t7) {
    t8 = <button type="button" onMouseEnter={t2} onMouseLeave={t3} className={t4} aria-label="Trusted attachment" aria-describedby={describedById} {...t5}>{t7}</button>;
    $[6] = describedById;
    $[7] = t4;
    $[8] = t5;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isTooltipOpen || $[12] !== showTooltip || $[13] !== tooltipId) {
    t9 = showTooltip && <Tooltip id={tooltipId} place="top" positionStrategy="fixed" offset={8} opacity={1} style={TOOLTIP_STYLES} isOpen={isTooltipOpen}><span className="tw-text-xs">Trusted — still use caution</span></Tooltip>;
    $[11] = isTooltipOpen;
    $[12] = showTooltip;
    $[13] = tooltipId;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}
function throwCsvPreviewSizeExceeded(onSizeExceeded: () => void): never {
  onSizeExceeded();
  throw new Error(CSV_PREVIEW_SIZE_EXCEEDED);
}
function assertCsvContentLengthWithinLimit(response: Response, maxChars: number, onSizeExceeded: () => void): void {
  const lengthHeader = response.headers.get("Content-Length");
  if (lengthHeader === null) {
    return;
  }
  const parsed = Number.parseInt(lengthHeader, 10);
  if (Number.isFinite(parsed) && parsed > maxChars) {
    throwCsvPreviewSizeExceeded(onSizeExceeded);
  }
}
async function readCsvPreviewTextFromBodyLessResponse(response: Response, maxChars: number, onSizeExceeded: () => void): Promise<string> {
  const text = await response.text();
  if (text.length > maxChars) {
    throwCsvPreviewSizeExceeded(onSizeExceeded);
  }
  return text;
}
function assertCsvPreviewSignalActive(signal: AbortSignal): void {
  if (signal.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}
function assertNoCsvDataBeyondCap(peek: ReadableStreamReadResult<Uint8Array>, signal: AbortSignal, decoder: TextDecoder, onSizeExceeded: () => void): void {
  assertCsvPreviewSignalActive(signal);
  if (peek.done || !peek.value) {
    return;
  }
  if (peek.value.byteLength > 0) {
    throwCsvPreviewSizeExceeded(onSizeExceeded);
  }
  const extra = decoder.decode(peek.value, {
    stream: true
  });
  if (extra.length > 0) {
    throwCsvPreviewSizeExceeded(onSizeExceeded);
  }
}
function finalizeCsvPreviewAtCap(result: string, peek: ReadableStreamReadResult<Uint8Array>, decoder: TextDecoder): string {
  return peek.done ? result + decoder.decode() : result;
}
async function releaseCsvPreviewReader(reader: ReadableStreamDefaultReader<Uint8Array>): Promise<void> {
  await reader.cancel().catch(() => undefined);
  try {
    reader.releaseLock();
  } catch {
    // Reader may already be released after cancellation or stream completion.
  }
}
async function readCsvPreviewFromStreamBody(reader: ReadableStreamDefaultReader<Uint8Array>, maxChars: number, signal: AbortSignal, onSizeExceeded: () => void): Promise<string> {
  const decoder = new TextDecoder();
  let result = "";
  for (;;) {
    const {
      done,
      value
    } = await reader.read();
    if (done) {
      return result + decoder.decode();
    }
    assertCsvPreviewSignalActive(signal);
    const decodedChunk = decoder.decode(value, {
      stream: true
    });
    const remaining = maxChars - result.length;
    if (remaining <= 0) {
      throwCsvPreviewSizeExceeded(onSizeExceeded);
    }
    if (decodedChunk.length > remaining) {
      throwCsvPreviewSizeExceeded(onSizeExceeded);
    }
    result += decodedChunk;
    if (result.length !== maxChars) {
      continue;
    }
    const peek = await reader.read();
    assertCsvPreviewSignalActive(signal);
    assertNoCsvDataBeyondCap(peek, signal, decoder, onSizeExceeded);
    return finalizeCsvPreviewAtCap(result, peek, decoder);
  }
}
async function readCsvPreviewText(response: Response, maxChars: number, signal: AbortSignal, onSizeExceeded: () => void): Promise<string> {
  assertCsvContentLengthWithinLimit(response, maxChars, onSizeExceeded);
  if (!response.body) {
    return readCsvPreviewTextFromBodyLessResponse(response, maxChars, onSizeExceeded);
  }
  const reader = response.body.getReader();
  try {
    return await readCsvPreviewFromStreamBody(reader, maxChars, signal, onSizeExceeded);
  } finally {
    await releaseCsvPreviewReader(reader);
  }
}
async function fetchCsvPreviewText(url: string, signal: AbortSignal, markSizeExceeded: () => void): Promise<string> {
  const response = await fetch(url, {
    signal
  });
  if (!response.ok) {
    throw new Error("Unable to load CSV preview.");
  }
  return readCsvPreviewText(response, CSV_PREVIEW_MAX_CHARS, signal, markSizeExceeded);
}
function resolveCsvPreviewErrorMessage(err: unknown, aborted: boolean, didTimeout: boolean, didExceedSize: boolean): string | undefined {
  if (aborted) {
    if (didTimeout) {
      return CSV_PREVIEW_TIMEOUT_MESSAGE;
    }
    if (didExceedSize) {
      return CSV_PREVIEW_SIZE_MESSAGE;
    }
    return undefined;
  }
  if (err instanceof Error && err.message === CSV_PREVIEW_SIZE_EXCEEDED) {
    return CSV_PREVIEW_SIZE_MESSAGE;
  }
  return err instanceof Error ? err.message : "Unable to load CSV.";
}
const ATTACHMENT_DOWNLOAD_FETCH_TIMEOUT_MS = 120_000;
function getPathnameFileExtension(url: string): string | null {
  try {
    const base = globalThis.window?.location?.origin || "https://6529.io";
    const parsed = new URL(url, base);
    const basename = parsed.pathname.split("/").pop() ?? "";
    const lastDot = basename.lastIndexOf(".");
    if (lastDot <= 0 || lastDot === basename.length - 1) {
      return null;
    }
    return basename.slice(lastDot + 1).toLowerCase();
  } catch {
    return null;
  }
}
function getAttachmentRenderType(mimeType: string, url: string): AttachmentRenderType {
  const normalizedMimeType = mimeType.split(";")[0]?.trim().toLowerCase();
  const pathExtension = getPathnameFileExtension(url);
  if (normalizedMimeType === "application/pdf" || pathExtension === "pdf") {
    return "pdf";
  }
  if (normalizedMimeType === "text/csv" || normalizedMimeType === "application/csv" || normalizedMimeType === "application/vnd.ms-excel" || pathExtension === "csv") {
    return "csv";
  }
  return "unknown";
}
function getAttachmentLabel(renderType: AttachmentRenderType): string {
  if (renderType === "pdf") return "PDF";
  if (renderType === "csv") return "CSV";
  return "File";
}
function getFallbackExtension(renderType: AttachmentRenderType): string {
  if (renderType === "pdf") return "pdf";
  if (renderType === "csv") return "csv";
  return "";
}
function resolveAttachmentFileName(fileInfo: ReturnType<typeof getFileInfoFromUrl>, fallbackExtension: string): string {
  if (fileInfo) {
    return `${fileInfo.name}.${fileInfo.extension}`;
  }
  if (fallbackExtension) {
    return `attachment.${fallbackExtension}`;
  }
  return "attachment";
}
interface CsvParserState {
  rows: string[][];
  row: string[];
  field: string;
  inQuotes: boolean;
}
function flushField(state: CsvParserState) {
  state.row.push(state.field);
  state.field = "";
}
function flushRow(state: CsvParserState): string[][] | null {
  flushField(state);
  state.rows.push(state.row);
  if (state.rows.length >= CSV_PREVIEW_MAX_ROWS) {
    return state.rows;
  }
  state.row = [];
  return null;
}
function processCsvQuote(state: CsvParserState, char: string, nextChar: string | undefined): number {
  if (char !== '"') {
    return 0;
  }
  if (state.inQuotes && nextChar === '"') {
    state.field += '"';
    return 2;
  }
  state.inQuotes = !state.inQuotes;
  return 1;
}
function processCsvDelimiter(state: CsvParserState, char: string): boolean {
  if (char === "," && !state.inQuotes) {
    flushField(state);
    return true;
  }
  return false;
}
function processCsvLineBreak(state: CsvParserState, char: string, nextChar: string | undefined): {
  consumed: number;
  rows: string[][] | null;
} {
  if ((char === "\n" || char === "\r") && !state.inQuotes) {
    return {
      consumed: char === "\r" && nextChar === "\n" ? 2 : 1,
      rows: flushRow(state)
    };
  }
  return {
    consumed: 0,
    rows: null
  };
}
function parseCsvPreview(text: string): string[][] {
  const state: CsvParserState = {
    rows: [],
    row: [],
    field: "",
    inQuotes: false
  };
  for (let index = 0; index < text.length; index++) {
    const char = text.charAt(index);
    const nextChar = index + 1 < text.length ? text.charAt(index + 1) : undefined;
    const quoteConsumed = processCsvQuote(state, char, nextChar);
    if (quoteConsumed > 0) {
      index += quoteConsumed - 1;
      continue;
    }
    if (processCsvDelimiter(state, char)) {
      continue;
    }
    const lineBreakResult = processCsvLineBreak(state, char, nextChar);
    if (lineBreakResult.consumed > 0) {
      if (lineBreakResult.rows) {
        return lineBreakResult.rows;
      }
      index += lineBreakResult.consumed - 1;
      continue;
    }
    state.field += char;
  }
  if (!state.inQuotes && (state.field.length || state.row.length)) {
    flushField(state);
    state.rows.push(state.row);
  }
  return state.rows;
}
function CsvAttachmentPreview({
  url
}: {
  readonly url: string;
}) {
  const [rows, setRows] = useState<string[][] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setRows(null);
    setError(null);
    const controller = new AbortController();
    let active = true;
    let didTimeout = false;
    let didExceedSize = false;
    const markSizeExceeded = () => {
      didExceedSize = true;
      controller.abort();
    };
    const timeoutId = globalThis.window.setTimeout(() => {
      didTimeout = true;
      controller.abort();
    }, ATTACHMENT_DOWNLOAD_FETCH_TIMEOUT_MS);
    void (async () => {
      try {
        const text = await fetchCsvPreviewText(url, controller.signal, markSizeExceeded);
        if (!active || controller.signal.aborted) {
          return;
        }
        setRows(parseCsvPreview(text));
      } catch (err: unknown) {
        if (!active) {
          return;
        }
        const message = resolveCsvPreviewErrorMessage(err, controller.signal.aborted, didTimeout, didExceedSize);
        if (message) {
          setError(message);
        }
      } finally {
        globalThis.window.clearTimeout(timeoutId);
      }
    })();
    return () => {
      active = false;
      globalThis.window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [url]);
  if (error) {
    return <div className="tw-rounded-b-lg tw-border tw-border-t-0 tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-p-4 tw-text-sm tw-text-iron-300">
        {error}
      </div>;
  }
  if (!rows) {
    return <div className="tw-rounded-b-lg tw-border tw-border-t-0 tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-p-4 tw-text-sm tw-text-iron-400">
        Loading CSV...
      </div>;
  }
  if (!rows.length) {
    return <div className="tw-rounded-b-lg tw-border tw-border-t-0 tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-p-4 tw-text-sm tw-text-iron-400">
        Empty CSV
      </div>;
  }
  const visibleRows = rows.map(row => row.slice(0, CSV_PREVIEW_MAX_COLUMNS));
  const rowKeyCounts = new Map<string, number>();
  let isFirstRow = true;
  return <div className="tw-overflow-x-auto tw-rounded-b-lg tw-border tw-border-t-0 tw-border-solid tw-border-iron-700 tw-bg-iron-950">
      <table className="tw-w-full tw-border-separate tw-border-spacing-0 tw-text-left tw-text-xs tw-text-iron-200">
        <tbody>
          {visibleRows.map(row_0 => {
          const rowSignature = row_0.join("\u0001");
          const rowCount = rowKeyCounts.get(rowSignature) ?? 0;
          rowKeyCounts.set(rowSignature, rowCount + 1);
          const cellKeyCounts = new Map<string, number>();
          const rowIsHeader = isFirstRow;
          isFirstRow = false;
          return <tr key={`csv-row-${rowSignature}-${rowCount}`}>
                {row_0.map(cell => {
              const cellCount = cellKeyCounts.get(cell) ?? 0;
              cellKeyCounts.set(cell, cellCount + 1);
              return <td key={`csv-cell-${cell}-${cellCount}`} className={clsx("tw-max-w-64 tw-border-0 tw-border-b tw-border-r tw-border-solid tw-border-iron-800 tw-px-3 tw-py-2 tw-align-top", rowIsHeader && "tw-bg-iron-900 tw-font-semibold")}>
                      <span className="tw-line-clamp-4 tw-break-words">
                        {cell}
                      </span>
                    </td>;
            })}
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}
function AnimatedAttachmentPanel(t0) {
  const $ = _c(16);
  const {
    isOpen,
    children
  } = t0;
  const [shouldRender, setShouldRender] = useState(isOpen);
  let t1;
  let t2;
  if ($[0] !== isOpen) {
    t1 = () => {
      if (isOpen) {
        setShouldRender(true);
        return;
      }
      const timeoutId = globalThis.window.setTimeout(() => setShouldRender(false), 220);
      return () => globalThis.window.clearTimeout(timeoutId);
    };
    t2 = [isOpen];
    $[0] = isOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!shouldRender && !isOpen) {
    return null;
  }
  const t3 = isOpen ? "tw-opacity-100" : "tw-opacity-0";
  let t4;
  if ($[3] !== t3) {
    t4 = clsx("tw-grid tw-transition-all tw-duration-200 tw-ease-out", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = isOpen ? "1fr" : "0fr";
  let t6;
  if ($[5] !== t5) {
    t6 = {
      gridTemplateRows: t5
    };
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = isOpen ? "tw-translate-y-0" : "-tw-translate-y-2";
  let t8;
  if ($[7] !== t7) {
    t8 = clsx("tw-transform-gpu tw-transition-transform tw-duration-200 tw-ease-out", t7);
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== children || $[10] !== t8) {
    t9 = <div className="tw-min-h-0 tw-overflow-hidden"><div className={t8}>{children}</div></div>;
    $[9] = children;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t4 || $[13] !== t6 || $[14] !== t9) {
    t10 = <div className={t4} style={t6}>{t9}</div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}
function AttachmentMoreMenu(t0) {
  const $ = _c(40);
  const {
    isOpen,
    hasMetadata,
    isDetailsOpen,
    copiedLink,
    isDownloading,
    buttonRef,
    onToggle,
    onToggleDetails,
    onCopyLink,
    onDownload
  } = t0;
  const getMenuItemClassName = _temp;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <EllipsisHorizontalIcon className="tw-size-4" aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== buttonRef || $[2] !== onToggle) {
    t2 = <button ref={buttonRef} type="button" onClick={onToggle} aria-label="Attachment options" title="More" className="tw-inline-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-text-iron-100 tw-transition desktop-hover:hover:tw-bg-iron-700">{t1}</button>;
    $[1] = buttonRef;
    $[2] = onToggle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isOpen || $[5] !== onToggle) {
    t3 = open => {
      if (!open && isOpen) {
        onToggle();
      }
    };
    $[4] = isOpen;
    $[5] = onToggle;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== hasMetadata || $[8] !== isDetailsOpen || $[9] !== onToggleDetails) {
    t4 = hasMetadata && <button type="button" onClick={onToggleDetails} className={getMenuItemClassName()}><CodeBracketSquareIcon className="tw-size-4 tw-flex-shrink-0" aria-hidden="true" /><span className="tw-text-sm tw-font-medium">{isDetailsOpen ? "Hide metadata" : "View metadata"}</span></button>;
    $[7] = hasMetadata;
    $[8] = isDetailsOpen;
    $[9] = onToggleDetails;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== copiedLink) {
    t5 = getMenuItemClassName(copiedLink);
    $[11] = copiedLink;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LinkIcon className="tw-size-4 tw-flex-shrink-0" aria-hidden="true" />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = copiedLink ? "Copied" : "Copy link";
  let t8;
  if ($[14] !== t7) {
    t8 = <span className="tw-text-sm tw-font-medium">{t7}</span>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== onCopyLink || $[17] !== t5 || $[18] !== t8) {
    t9 = <button type="button" onClick={onCopyLink} className={t5}>{t6}{t8}</button>;
    $[16] = onCopyLink;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = getMenuItemClassName();
    t11 = <ArrowDownTrayIcon className="tw-size-4 tw-flex-shrink-0" aria-hidden="true" />;
    $[20] = t10;
    $[21] = t11;
  } else {
    t10 = $[20];
    t11 = $[21];
  }
  const t12 = isDownloading ? "Downloading" : "Download";
  let t13;
  if ($[22] !== t12) {
    t13 = <span className="tw-text-sm tw-font-medium">{t12}</span>;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== isDownloading || $[25] !== onDownload || $[26] !== t13) {
    t14 = <button type="button" onClick={onDownload} disabled={isDownloading} className={t10}>{t11}{t13}</button>;
    $[24] = isDownloading;
    $[25] = onDownload;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== t4 || $[30] !== t9) {
    t15 = <li className="tw-list-none"><div className="tw-flex tw-flex-col tw-gap-y-1 tw-py-1">{t4}{t9}{t14}</div></li>;
    $[28] = t14;
    $[29] = t4;
    $[30] = t9;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== buttonRef || $[33] !== isOpen || $[34] !== t15 || $[35] !== t3) {
    t16 = <CommonDropdownItemsDefaultWrapper isOpen={isOpen} setOpen={t3} buttonRef={buttonRef}>{t15}</CommonDropdownItemsDefaultWrapper>;
    $[32] = buttonRef;
    $[33] = isOpen;
    $[34] = t15;
    $[35] = t3;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t16 || $[38] !== t2) {
    t17 = <>{t2}{t16}</>;
    $[37] = t16;
    $[38] = t2;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
}
function _temp(t0) {
  const active = t0 === undefined ? false : t0;
  return clsx("tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-x-3 tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-transition-colors tw-duration-200 desktop-hover:hover:tw-bg-iron-800", active ? "tw-text-primary-300 desktop-hover:hover:tw-text-primary-300" : "tw-text-iron-300 desktop-hover:hover:tw-text-iron-300");
}
export default function DropAttachmentDisplay({
  mimeType,
  attachmentUrl,
  fileName: providedFileName,
  disableMediaInteraction = false
}: {
  readonly mimeType: string;
  readonly attachmentUrl: string;
  readonly fileName?: string | undefined;
  readonly disableMediaInteraction?: boolean | undefined;
}) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const downloadAbortRef = useRef<AbortController | null>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const {
    isCapacitor
  } = useCapacitor();
  const safeAttachmentUrl = useMemo(() => getSafeAttachmentUrl(attachmentUrl), [attachmentUrl]);
  const safeMetadataUrl = useMemo(() => {
    const metadataUrl = getAttachmentMetadataUrl(attachmentUrl);
    return metadataUrl ? getSafeAttachmentUrl(metadataUrl) : null;
  }, [attachmentUrl]);
  const isRendered = isPreviewOpen;
  const {
    renderType,
    fileName,
    label,
    canRender,
    Icon
  } = useMemo(() => {
    const nextRenderType = getAttachmentRenderType(mimeType, attachmentUrl);
    const fileInfo = getFileInfoFromUrl(attachmentUrl);
    const fallbackExtension = getFallbackExtension(nextRenderType);
    const nextFileName = providedFileName ?? resolveAttachmentFileName(fileInfo, fallbackExtension);
    const nextLabel = getAttachmentLabel(nextRenderType);
    const nextCanRender = safeAttachmentUrl !== null && (nextRenderType === "pdf" || nextRenderType === "csv");
    const NextIcon = nextRenderType === "csv" ? TableCellsIcon : DocumentIcon;
    return {
      renderType: nextRenderType,
      fileName: nextFileName,
      label: nextLabel,
      canRender: nextCanRender,
      Icon: NextIcon
    };
  }, [attachmentUrl, mimeType, providedFileName, safeAttachmentUrl]);
  useEffect(() => () => {
    downloadAbortRef.current?.abort();
    downloadAbortRef.current = null;
  }, []);
  useEffect(() => {
    if (!copiedLink) {
      return;
    }
    const timeoutId = globalThis.window.setTimeout(() => setCopiedLink(false), 1500);
    return () => globalThis.window.clearTimeout(timeoutId);
  }, [copiedLink]);
  const handleDownload = async () => {
    if (isDownloading || !safeAttachmentUrl) {
      return;
    }
    setIsDownloading(true);
    const controller = new AbortController();
    downloadAbortRef.current = controller;
    const timeoutId_0 = globalThis.window.setTimeout(() => {
      controller.abort();
    }, ATTACHMENT_DOWNLOAD_FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(safeAttachmentUrl, {
        signal: controller.signal
      });
      if (!response.ok) {
        throw new Error("Unable to download attachment.");
      }
      const blob = await response.blob();
      if (isCapacitor) {
        await shareFetchedBlobInNativeApp(blob, fileName);
      } else {
        const objectUrl = URL.createObjectURL(blob);
        const anchor_0 = document.createElement("a");
        anchor_0.href = objectUrl;
        anchor_0.download = fileName;
        document.body.append(anchor_0);
        anchor_0.click();
        anchor_0.remove();
        URL.revokeObjectURL(objectUrl);
      }
    } catch {
      if (controller.signal.aborted || downloadAbortRef.current !== controller) {
        return;
      }
      const anchor = document.createElement("a");
      anchor.href = safeAttachmentUrl;
      anchor.download = fileName;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      globalThis.window.clearTimeout(timeoutId_0);
      if (downloadAbortRef.current === controller) {
        downloadAbortRef.current = null;
      }
      setIsDownloading(false);
    }
  };
  const handleCopyLink = async () => {
    if (!safeAttachmentUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(safeAttachmentUrl);
      setCopiedLink(true);
    } catch (error) {
      console.error("Failed to copy attachment link", error);
    }
  };
  const handleToggleDetails = () => {
    setIsDetailsOpen(current => !current);
    setIsMoreMenuOpen(false);
  };
  const handleToggleMoreMenu = () => {
    if (!isMoreMenuOpen) {
      setCopiedLink(false);
    }
    setIsMoreMenuOpen(current_0 => !current_0);
  };
  const handleMenuCopyLink = async () => {
    await handleCopyLink();
    globalThis.window.setTimeout(() => {
      setIsMoreMenuOpen(false);
    }, 300);
  };
  const handleMenuDownload = () => {
    void handleDownload();
    setIsMoreMenuOpen(false);
  };
  return <div className="tw-flex tw-w-full tw-flex-col">
      <div className={clsx("tw-flex tw-w-full tw-items-center tw-gap-x-3 tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-p-3", isRendered && "tw-rounded-b-none")}>
        <div className="tw-flex tw-size-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-iron-800 tw-text-iron-300">
          <Icon className="tw-size-6" aria-hidden="true" />
        </div>
        <div className="tw-min-w-0 tw-flex-1 tw-space-y-1">
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-x-1.5">
            <div className="tw-min-w-0 tw-truncate tw-text-sm tw-font-medium tw-text-iron-100">
              {fileName}
            </div>
            <div className="tw-flex-shrink-0">
              <TrustedAttachmentBadge />
            </div>
          </div>
          <div className="tw-text-xs tw-font-medium tw-text-iron-500">
            {label}
          </div>
        </div>
        {!disableMediaInteraction && <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-x-2">
            {canRender && <button type="button" onClick={() => setIsPreviewOpen(current_1 => !current_1)} aria-label={isPreviewOpen ? "Hide attachment preview" : "Render attachment preview"} title={isPreviewOpen ? "Hide preview" : "Render preview"} className="tw-inline-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-text-iron-100 tw-transition desktop-hover:hover:tw-bg-iron-700">
                {isPreviewOpen ? <EyeSlashIcon className="tw-size-4" aria-hidden="true" /> : <EyeIcon className="tw-size-4" aria-hidden="true" />}
              </button>}
            {safeAttachmentUrl && <AttachmentMoreMenu isOpen={isMoreMenuOpen} hasMetadata={safeMetadataUrl !== null} isDetailsOpen={isDetailsOpen} copiedLink={copiedLink} isDownloading={isDownloading} buttonRef={moreButtonRef} onToggle={handleToggleMoreMenu} onToggleDetails={handleToggleDetails} onCopyLink={handleMenuCopyLink} onDownload={handleMenuDownload} />}
          </div>}
      </div>
      <AnimatedAttachmentPanel isOpen={isDetailsOpen && !!safeMetadataUrl}>
        {safeMetadataUrl && <div className="tw-rounded-b-lg tw-border tw-border-t-0 tw-border-solid tw-border-iron-700 tw-bg-iron-950">
            <JsonPreview link={safeMetadataUrl} onClose={() => setIsDetailsOpen(false)} />
          </div>}
      </AnimatedAttachmentPanel>
      <AnimatedAttachmentPanel isOpen={isPreviewOpen && !!safeAttachmentUrl}>
        {renderType === "pdf" && safeAttachmentUrl && <iframe src={safeAttachmentUrl} title={fileName} referrerPolicy="no-referrer" className="tw-h-[32rem] tw-w-full tw-rounded-b-lg tw-border tw-border-t-0 tw-border-solid tw-border-iron-700 tw-bg-iron-950" />}
        {renderType === "csv" && safeAttachmentUrl && <CsvAttachmentPreview url={safeAttachmentUrl} />}
      </AnimatedAttachmentPanel>
    </div>;
}